'use client';

import React from 'react';
import { useSelector } from 'react-redux';

import Image from 'next/image';
import Link from 'next/link';

import IconWithLink from '@/components/elements/icon/icon-with-link';
import { useTranslation } from '@/i18n/client';
import { CountryCode } from '@/i18n/countries';
import { selectIsLoggedIn, selectUserName } from '@/store/auth';
import { assetsShared, externalLinks } from '@/utils/constants';
import { buildUrl } from '@/utils/navigation';
import { createTestId } from '@/utils/string-utils';

import { ScrollLink } from '../elements/scroll-link';
import { HeaderPopupKeys, HeaderProps } from './header.types';
import SwitcherLanguage from './switcher-language';

const createrHeaderTestId = ({ name }: { name: string }) => createTestId({ component: 'header', name });

export default function HeaderMobile(props: HeaderProps) {
    const { t: tCo } = useTranslation(props.lang, 'common');
    const { t: tAu } = useTranslation(props.lang, 'auth');

    const isLoggedIn = useSelector(selectIsLoggedIn);
    const userName = useSelector(selectUserName);

    const country = props.country?.slug || 'serbia';

    const closeAll = () => {
        props.onClose(HeaderPopupKeys.auth);
        props.onClose(HeaderPopupKeys.navbar);
    };

    const closeNavBar = () => {
        props.onClose(HeaderPopupKeys.navbar);
    };

    return (
        <>
            <nav className="my-auto ml-auto mr-2 flex gap-2 sm:mr-6">
                <IconWithLink
                    testId={createrHeaderTestId({ name: 'telegram-link' })}
                    size={6}
                    ariaLabel="Contact us via telegram"
                    icon={'bg-telegram'}
                    href={externalLinks.telegram}
                />
                <IconWithLink
                    testId={createrHeaderTestId({ name: 'instagram-link' })}
                    size={6}
                    ariaLabel="Link to Instagram"
                    icon={'bg-instagram'}
                    href={externalLinks.instagram}
                />
                <IconWithLink
                    testId={createrHeaderTestId({ name: 'email-link' })}
                    size={6}
                    ariaLabel="Contact us via email"
                    icon={'bg-mail'}
                    href={`mailto:${externalLinks.email}`}
                />
                <IconWithLink
                    testId={createrHeaderTestId({ name: 'viber-link' })}
                    size={6}
                    ariaLabel="Contact us via Viber"
                    icon={'bg-viber'}
                    href={tCo(`contacts.${props.country?.code as CountryCode}.viber`)}
                    fallbackUrl={externalLinks.viber_download}
                />
                <IconWithLink
                    testId={createrHeaderTestId({ name: 'whatsapp-link' })}
                    size={6}
                    ariaLabel="Contact us via WhatsApp"
                    icon={'bg-whatsapp'}
                    href={tCo(`contacts.${props.country?.code as CountryCode}.whatsapp`)}
                />
            </nav>
            {isLoggedIn ? (
                <div
                    data-testid={createrHeaderTestId({ name: 'menu-btn' })}
                    className="square-button w-fit gap-x-4 rounded rounded-l-full py-2 pl-5 pr-4"
                    onClick={() => props.onOpen(HeaderPopupKeys.navbar)}
                >
                    {userName && (
                        <div className="flex h-6 w-6 justify-center rounded-full bg-accent">
                            <span className="m-auto block capitalize text-white">{userName.slice(0, 1) || ' '}</span>
                        </div>
                    )}
                    <span className="icon bg-menu"></span>
                </div>
            ) : (
                <div
                    data-testid={createrHeaderTestId({
                        name: props.isVisible.auth ? 'header-close-menu-btn' : 'header-menu-btn',
                    })}
                    className="square-button"
                    onClick={() => (props.isVisible.auth ? closeAll() : props.onOpen(HeaderPopupKeys.navbar))}
                >
                    <span className={`icon ${props.isVisible.auth ? 'bg-close' : 'bg-menu'} `}></span>
                </div>
            )}
            {/*<div
                className="square-button"
                onClick={() => (props.isVisible.auth ? closeAll() : props.onOpen(HeaderPopupKeys.navbar))}
            >
                <span className={`icon ${props.isVisible.auth ? 'bg-close' : 'bg-menu'} `}></span>
            </div>*/}
            {props.isVisible[HeaderPopupKeys.navbar] && (
                <div className="fixed bottom-0 left-0 right-0 top-0 z-[3] h-full bg-white">
                    <div className="flex items-center justify-between border-b border-black border-opacity-80 px-4 py-5">
                        <Link href={buildUrl('', { children: [country] })}>
                            <Image
                                className="h-[3.625rem] w-[13.35rem] cursor-pointer xs:h-[2.75rem] xs:w-[10.125rem]"
                                width={213.6}
                                height={58}
                                src={assetsShared.logoPrimary}
                                alt="logo"
                            />
                        </Link>
                        <div
                            data-testid={createrHeaderTestId({ name: 'close-menu-btn' })}
                            onClick={closeAll}
                            className="square-button"
                        >
                            <span className="icon bg-close"></span>
                        </div>
                    </div>
                    <div className="grid grid-flow-row gap-4 px-4 pt-5 text-center">
                        {/* {props.country?.code && (
                            <div className="z-[11] justify-self-center">
                                <SwitcherCountry countryCode={props.country.code} />
                            </div>
                        )} */}
                        {props.country?.slug !== 'russia' && (
                            <div className="justify-self-center">
                                <SwitcherLanguage lang={props.lang} />
                            </div>
                        )}
                        <nav className="mt-6 flex flex-col gap-y-3">
                            <Link
                                data-testid={createrHeaderTestId({ name: 'about-link' })}
                                onClick={closeNavBar}
                                className="text-regular whitespace-nowrap"
                                prefetch={false}
                                href={buildUrl('about', { lang: props.lang })}
                                scroll={false}
                            >
                                {tCo('about')}
                            </Link>
                            <Link
                                data-testid={createrHeaderTestId({ name: 'collaboration-link' })}
                                onClick={closeNavBar}
                                prefetch={false}
                                className="text-regular whitespace-nowrap"
                                href={buildUrl('collaboration', { lang: props.lang })}
                                scroll={false}
                            >
                                {tCo('collaboration')}
                            </Link>
                            {props.country?.slug !== 'russia' && (
                                <Link
                                    data-testid={createrHeaderTestId({ name: 'services-link' })}
                                    onClick={closeNavBar}
                                    className="text-regular whitespace-nowrap"
                                    prefetch={false}
                                    href={buildUrl('', { children: [country, 'services'] })}
                                    scroll={false}
                                >
                                    {tCo('pServices')}
                                </Link>
                            )}
                            {props.isCalendar ? (
                                <ScrollLink
                                    data-testid={createrHeaderTestId({ name: 'main-link' })}
                                    className="text-regular whitespace-nowrap"
                                    prefetch={false}
                                    onClick={() => props.onClose(HeaderPopupKeys.auth)}
                                    href={buildUrl('', { children: [country], fragment: 'events' })}
                                    smooth={true}
                                    scroll={false}
                                >
                                    {tCo('events')}
                                </ScrollLink>
                            ) : (
                                <Link
                                    data-testid={createrHeaderTestId({ name: 'main-link' })}
                                    onClick={closeNavBar}
                                    className="text-regular whitespace-nowrap"
                                    prefetch={false}
                                    href={buildUrl('', { children: [country] })}
                                    scroll={false}
                                >
                                    {tCo('events')}
                                </Link>
                            )}
                        </nav>
                        {isLoggedIn ? (
                            <>
                                <Link
                                    data-testid={createrHeaderTestId({ name: 'user-cabinet-link' })}
                                    href={buildUrl('user', { lang: props.lang })}
                                    className="border-regular mx-auto flex w-1/2 cursor-pointer items-center justify-center gap-x-4 rounded-full px-5 py-2"
                                >
                                    {userName && (
                                        <div className="h-6 w-6 rounded-full bg-accent">
                                            <span className="m-auto block uppercase text-white">
                                                {userName.slice(0, 1) || ''}
                                            </span>
                                        </div>
                                    )}
                                    <p className="text text-sm">{tAu('buttons.profile')}</p>
                                </Link>
                            </>
                        ) : (
                            <button
                                data-testid={createrHeaderTestId({ name: 'auth-btn' })}
                                aria-label={'open login popup'}
                                className="btn-primary mx-auto w-3/4 sm:w-1/2"
                                onClick={() => props.onOpen(HeaderPopupKeys.auth)}
                            >
                                {tAu('buttons.loginShort')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
